(() => {
  'use strict';
  if (window.scHome) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new WeakMap();
  const find = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];
  const setup = (scope, selector, build) => {
    find(scope, selector).forEach((element) => {
      if (cleanups.has(element)) return;
      cleanups.set(element, build(element) || (() => {}));
    });
  };
  function carousel(root) {
    const track = root.querySelector('[data-sc-track]');
    const previous = root.querySelector('[data-sc-prev]');
    const next = root.querySelector('[data-sc-next]');
    const controls = root.querySelector('[data-sc-controls]');
    if (!track || !previous || !next) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
      if (controls) controls.hidden = max <= 2;
    };
    const move = (direction) => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
      track.scrollBy({ left: direction * step, behavior: motion.matches ? 'instant' : 'smooth' });
    };
    previous.addEventListener('click', () => move(-1), options);
    next.addEventListener('click', () => move(1), options);
    track.addEventListener('scroll', update, { ...options, passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      }
    }, options);
    const resize = new ResizeObserver(update);
    resize.observe(track);
    root.dataset.scReady = '';
    update();
    return () => { controller.abort(); resize.disconnect(); };
  }
  function videoPlayer(video) {
    const parent = video.parentElement;
    const toggle = parent.querySelector('[data-sc-video-toggle]');
    const pauseIcon = toggle?.querySelector('[data-sc-video-pause]');
    const playIcon = toggle?.querySelector('[data-sc-video-play]');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    let visible = false;
    let userPaused = false;
    let userStarted = false;
    let loaded = false;
    let pending = false;
    const update = () => {
      const playing = !video.paused;
      video.classList.toggle('is-playing', playing);
      toggle?.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      if (pauseIcon) pauseIcon.hidden = !playing;
      if (playIcon) playIcon.hidden = playing;
    };
    const play = () => {
      if (!visible || document.hidden || userPaused || (motion.matches && !userStarted)) return;
      if (pending || !video.paused) return;
      if (!loaded) {
        video.poster = parent.querySelector('picture img, .sc-feed__image')?.currentSrc || video.dataset.scPoster;
        video.querySelectorAll('[data-sc-src]').forEach((source) => {
          if (!source.media || window.matchMedia(source.media).matches) source.src = source.dataset.scSrc;
        });
        video.load();
        loaded = true;
      }
      pending = true;
      video.play().catch(() => {}).finally(() => {
        pending = false;
        if (!visible || document.hidden || userPaused || (motion.matches && !userStarted)) video.pause();
        update();
      });
    };
    const observer = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .35;
      if (visible) play(); else video.pause();
    }, { threshold: [0, .35] });
    const observe = () => { if (video.isConnected) observer.observe(video); };
    if (document.readyState === 'complete') observe();
    else window.addEventListener('load', observe, { ...options, once: true });
    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        if (video.paused) { userPaused = false; userStarted = true; play(); }
        else { userPaused = true; video.pause(); }
      }, options);
    }
    video.addEventListener('playing', update, options);
    video.addEventListener('pause', update, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); else play(); }, options);
    motion.addEventListener('change', () => { if (motion.matches) { userStarted = false; video.pause(); } else play(); }, options);
    update();
    return () => { controller.abort(); observer.disconnect(); video.pause(); };
  }
  function productPopover(popover) {
    const trigger = document.querySelector(`[popovertarget="${popover.id}"][aria-haspopup]`);
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const position = () => {
      if (!trigger) return;
      const anchor = trigger.getBoundingClientRect();
      const width = popover.offsetWidth;
      const height = popover.offsetHeight;
      const left = Math.max(16, Math.min(anchor.left - width / 2 + anchor.width / 2, window.innerWidth - width - 16));
      const top = Math.max(16, Math.min(anchor.bottom + 12, window.innerHeight - height - 16));
      popover.style.left = `${left}px`;
      popover.style.top = `${top}px`;
    };
    popover.addEventListener('toggle', (event) => {
      const open = event.newState === 'open';
      trigger?.setAttribute('aria-expanded', String(open));
      if (open) { position(); popover.querySelector('button, a')?.focus({ preventScroll: true }); }
      else if (popover.contains(document.activeElement) || document.activeElement === document.body) trigger?.focus({ preventScroll: true });
    }, options);
    popover.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); popover.hidePopover(); trigger?.focus({ preventScroll: true }); }
    }, options);
    window.addEventListener('resize', position, options);
    // Phones, tablets and browsers without the Popover API: the (+) opens the product page instead of the card.
    const touchQuery = window.matchMedia('(max-width: 989px), (hover: none) and (pointer: coarse)');
    trigger?.addEventListener('click', (event) => {
      const href = trigger.dataset.scHref;
      if (!href) return;
      if (touchQuery.matches || !('popover' in HTMLElement.prototype)) {
        event.preventDefault();
        window.location.assign(href);
      }
    }, options);
    return () => controller.abort();
  }
  function offers(root) {
    const dialog = root.querySelector('dialog');
    const open = root.querySelector('[data-sc-offers-open]');
    const close = root.querySelector('[data-sc-offers-close]');
    const dismiss = root.querySelector('[data-sc-offers-dismiss]');
    const key = `sc-offers-dismissed:${dialog.id}`;
    if (typeof dialog.showModal !== 'function') return;
    try { if (sessionStorage.getItem(key)) return; } catch (_) { /* Storage may be unavailable. */ }
    const controller = new AbortController();
    const options = { signal: controller.signal };
    open.hidden = false;
    open.addEventListener('click', () => { dialog.showModal(); open.setAttribute('aria-expanded', 'true'); }, options);
    close.addEventListener('click', () => dialog.close(), options);
    dismiss.addEventListener('click', () => {
      try { sessionStorage.setItem(key, '1'); } catch (_) { /* Dismiss still works without storage. */ }
      open.hidden = true;
      dialog.close();
    }, options);
    dialog.addEventListener('click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    }, options);
    dialog.addEventListener('close', () => { open.setAttribute('aria-expanded', 'false'); if (!open.hidden) open.focus(); }, options);
    return () => { controller.abort(); if (dialog.open) dialog.close(); };
  }
  function marquee(root) {
    const toggle = root.querySelector('[data-sc-marquee-toggle]');
    const controller = new AbortController();
    toggle.hidden = motion.matches;
    toggle.addEventListener('click', () => {
      const paused = root.classList.toggle('is-paused');
      toggle.setAttribute('aria-label', paused ? 'Play marquee' : 'Pause marquee');
      toggle.querySelector('[data-sc-video-pause]').hidden = paused;
      toggle.querySelector('[data-sc-video-play]').hidden = !paused;
    }, { signal: controller.signal });
    motion.addEventListener('change', () => { toggle.hidden = motion.matches; }, { signal: controller.signal });
    return () => controller.abort();
  }
  function init(scope = document) {
    setup(scope, '[data-sc-carousel]', carousel);
    setup(scope, '[data-sc-video]', videoPlayer);
    setup(scope, '[data-sc-popover]', productPopover);
    setup(scope, '[data-sc-offers]', offers);
    setup(scope, '.sc-marquee', marquee);
  }
  window.scHome = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    find(event.target, '[data-sc-carousel], [data-sc-video], [data-sc-popover], [data-sc-offers], .sc-marquee').forEach((element) => { cleanups.get(element)?.(); cleanups.delete(element); });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const track = event.target.closest('[data-sc-track]');
    if (!track) return;
    const tile = [...track.children].find((child) => child === event.target || child.contains(event.target));
    if (tile) track.scrollTo({ left: tile.offsetLeft - track.offsetLeft, behavior: 'instant' });
  });
})();
